import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Eye, MousePointerClick, Users, TrendingUp, ExternalLink, RefreshCw, BarChart3 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return iso; }
}

function StatCard({ icon: Icon, label, value, sub, color = "#06B6D4", testid }) {
  return (
    <div className="bg-white border border-black/8 rounded-2xl p-4 flex items-start gap-3" data-testid={testid}>
      <div className="rounded-xl p-2.5" style={{ background: `${color}1A` }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold">{label}</div>
        <div className="display text-3xl font-extrabold text-[#0A1128] mt-0.5 tabular-nums">{Number(value || 0).toLocaleString("fr-FR")}</div>
        {sub && <div className="text-[11px] text-[#64748B] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/analytics");
      setData(r.data);
    } catch { toast.error("Impossible de charger les statistiques"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const chartData = useMemo(() => (data?.daily_30d || []).map((d) => ({ name: fmtDate(d.date + "T00:00:00Z"), views: d.views })), [data]);

  if (loading || !data) {
    return <div className="p-8 text-center text-[#64748B]" data-testid="analytics-loading">Chargement…</div>;
  }
  const t = data.totals || {};

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="admin-analytics">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold flex items-center gap-2"><BarChart3 size={26} className="text-[#06B6D4]" />Analytics & Trafic</h1>
          <p className="text-sm text-[#64748B] mt-1">Suivi des visites & clics sur le site public (anonymisé, RGPD-friendly).</p>
        </div>
        <Button onClick={load} variant="outline" data-testid="analytics-refresh"><RefreshCw size={14} className="mr-1.5" />Rafraîchir</Button>
      </div>

      {/* KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="analytics-kpis">
        <StatCard icon={Eye} label="Pages vues — 24h" value={t.page_views_24h} sub="Dernières 24h" color="#06B6D4" testid="kpi-views-24h" />
        <StatCard icon={Eye} label="Pages vues — 7j" value={t.page_views_7d} sub="7 derniers jours" color="#0A1128" testid="kpi-views-7d" />
        <StatCard icon={Eye} label="Pages vues — 30j" value={t.page_views_30d} sub="30 derniers jours" color="#94A3B8" testid="kpi-views-30d" />
        <StatCard icon={Users} label="Sessions uniques 7j" value={t.unique_sessions_7d} sub="Visiteurs distincts" color="#A7F3D0" testid="kpi-sessions-7d" />
        <StatCard icon={MousePointerClick} label="Clics tracés 7j" value={t.clicks_7d} sub="Boutons / CTA" color="#FFDCA8" testid="kpi-clicks-7d" />
      </section>

      {/* Daily chart */}
      <section className="bg-white border border-black/8 rounded-2xl p-5" data-testid="analytics-daily">
        <h2 className="display font-bold text-[#0A1128] flex items-center gap-2 mb-3"><TrendingUp size={18} className="text-[#06B6D4]" />Évolution sur 30 jours</h2>
        {chartData.length === 0 ? (
          <p className="text-sm text-[#64748B] italic py-8 text-center">Aucune donnée sur les 30 derniers jours.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="views" stroke="#06B6D4" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top pages */}
        <section className="bg-white border border-black/8 rounded-2xl p-5" data-testid="analytics-top-pages">
          <h2 className="display font-bold text-[#0A1128] flex items-center gap-2 mb-3"><Eye size={18} className="text-[#0A1128]" />Pages les plus visitées (7j)</h2>
          {(data.top_pages_7d || []).length === 0 ? (
            <p className="text-sm text-[#64748B] italic py-4">Aucune visite tracée sur 7j.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart layout="vertical" data={data.top_pages_7d.slice(0, 10)} margin={{ left: 10, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="path" tick={{ fontSize: 11 }} width={170} />
                <Tooltip />
                <Bar dataKey="views" fill="#06B6D4" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </section>

        {/* Top clicks */}
        <section className="bg-white border border-black/8 rounded-2xl p-5" data-testid="analytics-top-clicks">
          <h2 className="display font-bold text-[#0A1128] flex items-center gap-2 mb-3"><MousePointerClick size={18} className="text-[#FFDCA8]" />Boutons les plus cliqués (7j)</h2>
          {(data.top_clicks_7d || []).length === 0 ? (
            <p className="text-sm text-[#64748B] italic py-4">
              Aucun clic tracé pour le moment.<br/>
              <span className="text-xs">Astuce : ajoutez l&apos;attribut <code className="bg-[#FAFAFA] px-1 rounded">{`data-track="nom-bouton"`}</code> sur vos CTA pour qu&apos;ils remontent ici.</span>
            </p>
          ) : (
            <div className="space-y-1.5">
              {data.top_clicks_7d.slice(0, 10).map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-black/5 pb-1.5 last:border-0">
                  <span className="font-bold text-[#0A1128] truncate flex-1">{c.target}</span>
                  <span className="tabular-nums text-[#06B6D4] font-extrabold ml-2">{c.count}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Referrers */}
      <section className="bg-white border border-black/8 rounded-2xl p-5" data-testid="analytics-referrers">
        <h2 className="display font-bold text-[#0A1128] flex items-center gap-2 mb-3"><ExternalLink size={18} className="text-[#06B6D4]" />Sources externes (30j)</h2>
        {(data.referrers_30d || []).length === 0 ? (
          <p className="text-sm text-[#64748B] italic py-4">Aucun referrer enregistré sur 30j (les visites directes ne remontent pas ici).</p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.referrers_30d.map((r, i) => (
              <li key={i} className="flex items-center justify-between text-sm bg-[#FAFAFA] rounded-xl px-3 py-2 border border-black/5">
                <a href={r.source} target="_blank" rel="noopener noreferrer" className="truncate text-[#0A1128] hover:text-[#06B6D4] flex-1">{r.source}</a>
                <span className="tabular-nums font-extrabold ml-2 text-[#06B6D4]">{r.count}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
